import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, X, Edit2, Archive, Percent, History, RefreshCw, Eye, EyeOff } from "lucide-react";
import { getToken } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PricingPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);        // plan object or null
  const [previewPct, setPreviewPct] = useState(30);
  const [preview, setPreview] = useState(null);        // {pct, rows[]}
  const [showAudit, setShowAudit] = useState(null);    // plan.id
  const [audit, setAudit] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/plans`, { headers: authHdr() });
      setPlans(r.data?.plans || []);
    } catch (e) {
      toast.error("Could not load plans.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activeCount = useMemo(() => plans.filter((p) => p.active).length, [plans]);
  const publicCount = useMemo(() => plans.filter((p) => p.active && p.public).length, [plans]);

  const runPreview = async () => {
    try {
      const r = await axios.post(`${API}/admin/plans/preview-reduction`,
        { pct: Number(previewPct) }, { headers: authHdr() });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    }
  };

  const applyPreviewRow = async (row) => {
    const ok = window.confirm(
      `Change ${row.slug} from £${row.current_gbp} to £${row.proposed_gbp}?\n\n`
      + `This writes a new row to price_history and clears the Stripe Price ID\n`
      + `(a new Stripe Price will need to be minted via "Sync with Stripe").\n\n`
      + `Existing subscribers' pro_until grants are NOT changed.`
    );
    if (!ok) return;
    try {
      await axios.patch(`${API}/admin/plans/${row.plan_id}`,
        { amount_gbp: row.proposed_gbp, reason: `${preview.pct}% reduction preview` },
        { headers: authHdr() });
      toast.success(`Updated ${row.slug} → £${row.proposed_gbp}`);
      await load();
      // refresh preview with the new baseline
      await runPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  };

  const toggle = async (plan, field) => {
    try {
      await axios.patch(`${API}/admin/plans/${plan.id}`,
        { [field]: !plan[field], reason: `toggle ${field}` },
        { headers: authHdr() });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
  };

  const archive = async (plan) => {
    if (!window.confirm(`Archive ${plan.slug}? It will be hidden from /pricing and no new checkouts can use it.`)) return;
    try {
      await axios.post(`${API}/admin/plans/${plan.id}/archive`, {}, { headers: authHdr() });
      toast.success("Archived.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed.");
    }
  };

  const syncStripe = async (plan) => {
    try {
      const r = await axios.post(`${API}/admin/plans/${plan.id}/sync-stripe`, {}, { headers: authHdr() });
      toast.success(`Stripe Price minted: ${r.data.stripe_price_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe sync failed.");
    }
  };

  const openAudit = async (plan) => {
    setShowAudit(plan.id);
    try {
      const r = await axios.get(`${API}/admin/plans/audit`,
        { params: { entity_id: plan.id, limit: 50 }, headers: authHdr() });
      setAudit(r.data?.audit || []);
    } catch (e) {
      setAudit([]);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="animate-spin" size={14} />Loading plans…</div>;

  return (
    <div data-testid="pricing-plans-panel" className="space-y-6">
      {/* Legal footer wording reminder */}
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
        <strong>MyAE is a trading name of OpenDegree Ltd.</strong> Any price shown
        here is the authoritative amount used by /pricing, checkout and Stripe.
        Existing subscribers' pro_until grants are never auto-repriced.
      </div>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Tile label="Total plans" value={plans.length} testid="tile-total" />
        <Tile label="Active (purchasable)" value={activeCount} testid="tile-active" />
        <Tile label="Public (on /pricing)" value={publicCount} testid="tile-public" />
      </div>

      {/* 30% reduction preview card */}
      <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="reduction-preview">
        <div className="flex items-center gap-2 mb-3">
          <Percent size={14} className="text-indigo-600" />
          <h3 className="text-sm font-medium">Reduction preview</h3>
          <span className="text-xs text-zinc-500">Preview only — nothing is written until you click Apply on a row.</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-600">Percent off:</label>
          <input type="number" min="0" max="95" value={previewPct}
            onChange={(e) => setPreviewPct(e.target.value)}
            className="w-20 border px-2 py-1 text-sm font-mono" data-testid="preview-pct" />
          <button onClick={runPreview} className="bg-indigo-600 text-white px-3 py-1.5 text-xs" data-testid="preview-run">
            Calculate
          </button>
        </div>
        {preview && (
          <table className="mt-3 w-full text-xs" data-testid="preview-table">
            <thead className="text-zinc-500 text-left">
              <tr><th className="py-1">Slug</th><th>Current</th><th>Proposed ({preview.pct}% off)</th><th>Delta</th><th></th></tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.plan_id} className="border-t border-indigo-100">
                  <td className="py-1 font-mono">{r.slug}</td>
                  <td className="font-mono">£{r.current_gbp}</td>
                  <td className="font-mono text-emerald-700">£{r.proposed_gbp}</td>
                  <td className="font-mono text-rose-700">£{r.delta_gbp}</td>
                  <td>
                    <button onClick={() => applyPreviewRow(r)}
                      className="bg-emerald-600 text-white px-2 py-0.5 text-[11px]"
                      data-testid={`preview-apply-${r.slug}`}>Apply</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Plans table */}
      <section>
        <h3 className="text-sm font-medium mb-2">Plans</h3>
        <div className="overflow-x-auto border border-zinc-200">
          <table className="w-full text-sm" data-testid="plans-table">
            <thead className="bg-zinc-50 text-xs text-zinc-500 text-left">
              <tr>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Interval</th>
                <th className="px-3 py-2">Amount (GBP)</th>
                <th className="px-3 py-2">Stripe Price</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Public</th>
                <th className="px-3 py-2 w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} data-testid={`plan-row-${p.slug}`} className="border-t border-zinc-100">
                  <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                  <td className="px-3 py-2">{p.label}</td>
                  <td className="px-3 py-2 text-xs">{p.type}</td>
                  <td className="px-3 py-2 text-xs">{p.interval}</td>
                  <td className="px-3 py-2 font-mono">£{p.amount_gbp}</td>
                  <td className="px-3 py-2 text-xs">
                    {p.stripe_price_id ? (
                      <span className="text-emerald-700 font-mono">{p.stripe_price_id.slice(0, 14)}…</span>
                    ) : (
                      <span className="text-amber-700">needs sync</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggle(p, "active")}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}
                      data-testid={`toggle-active-${p.slug}`}>
                      {p.active ? <Check size={11} /> : <X size={11} />}{p.active ? "yes" : "no"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggle(p, "public")}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded ${p.public ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}
                      data-testid={`toggle-public-${p.slug}`}>
                      {p.public ? <Eye size={11} /> : <EyeOff size={11} />}{p.public ? "yes" : "no"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(p)} className="text-xs text-indigo-700 inline-flex items-center gap-0.5"
                        data-testid={`edit-${p.slug}`}><Edit2 size={11} />Edit</button>
                      <button onClick={() => syncStripe(p)} className="text-xs text-emerald-700 inline-flex items-center gap-0.5"
                        data-testid={`sync-${p.slug}`}><RefreshCw size={11} />Sync</button>
                      <button onClick={() => openAudit(p)} className="text-xs text-zinc-600 inline-flex items-center gap-0.5"
                        data-testid={`audit-${p.slug}`}><History size={11} />Audit</button>
                      <button onClick={() => archive(p)} className="text-xs text-rose-700 inline-flex items-center gap-0.5"
                        data-testid={`archive-${p.slug}`}><Archive size={11} />Archive</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Edit modal */}
      {editing && (
        <EditPlanModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {/* Audit modal */}
      {showAudit && (
        <AuditModal
          planId={showAudit}
          plan={plans.find((p) => p.id === showAudit)}
          rows={audit}
          onClose={() => { setShowAudit(null); setAudit([]); }}
        />
      )}
    </div>
  );
}

const Tile = ({ label, value, testid }) => (
  <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className="font-mono text-xl mt-1">{value}</p>
  </div>
);

const EditPlanModal = ({ plan, onClose, onSaved }) => {
  const [label, setLabel] = useState(plan.label);
  const [description, setDescription] = useState(plan.description || "");
  const [amount, setAmount] = useState(plan.amount_gbp);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const changed = label !== plan.label || description !== (plan.description || "") || amount !== plan.amount_gbp;
  const priceChanged = amount !== plan.amount_gbp;

  const save = async () => {
    if (!changed) { onClose(); return; }
    if (priceChanged && !reason.trim()) {
      toast.error("A reason is required when changing the price.");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/plans/${plan.id}`,
        { label, description, amount_gbp: amount, reason },
        { headers: authHdr() });
      toast.success("Plan updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-plan-modal">
      <div className="bg-white max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-medium mb-3">Edit plan — <span className="font-mono">{plan.slug}</span></h3>
        <label className="block text-xs text-zinc-600 mb-1">Label</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          className="w-full border px-2 py-1 text-sm mb-3" data-testid="edit-label" />
        <label className="block text-xs text-zinc-600 mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
          rows={2} className="w-full border px-2 py-1 text-sm mb-3" data-testid="edit-description" />
        <label className="block text-xs text-zinc-600 mb-1">Amount (GBP) — 2dp string</label>
        <input value={amount} onChange={(e) => setAmount(e.target.value)}
          className="w-full border px-2 py-1 text-sm font-mono mb-3" data-testid="edit-amount" />
        {priceChanged && (
          <>
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 mb-2">
              Price change: £{plan.amount_gbp} → £{amount}. This will append to price_history,
              clear stripe_price_id and NOT alter existing subscribers' pro_until.
            </div>
            <label className="block text-xs text-zinc-600 mb-1">Reason (required for price change)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. 30% public-launch reduction"
              className="w-full border px-2 py-1 text-sm mb-3" data-testid="edit-reason" />
          </>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={save} disabled={busy || !changed}
            className="bg-indigo-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="edit-save">
            {busy ? <Loader2 className="animate-spin inline" size={13} /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AuditModal = ({ plan, rows, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="audit-modal">
    <div className="bg-white max-w-2xl w-full p-5" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-sm font-medium mb-3">Audit log — <span className="font-mono">{plan?.slug}</span></h3>
      <div className="max-h-96 overflow-y-auto text-xs">
        {rows.length === 0 && <p className="text-zinc-500">No audit entries.</p>}
        {rows.map((r) => (
          <div key={r.id} className="border-t py-2">
            <div className="flex justify-between">
              <span className="font-mono text-indigo-700">{r.type}</span>
              <span className="text-zinc-500">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            <div className="text-zinc-600 mt-0.5">by {r.actor_email || r.actor_user_id}</div>
            {r.reason && <div className="text-zinc-700 italic">"{r.reason}"</div>}
            {r.before && <div className="font-mono text-[10px] text-rose-700 mt-1">before: {JSON.stringify(r.before)}</div>}
            {r.after && <div className="font-mono text-[10px] text-emerald-700">after: {JSON.stringify(r.after)}</div>}
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={onClose} className="px-3 py-1.5 text-sm">Close</button>
      </div>
    </div>
  </div>
);
